import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

//Import Mixpanel SDK
import mixpanel from "mixpanel-browser";

// Near entry of your product, init Mixpanel.
// Deferred to idle time so the analytics SDK (init + session-replay wiring) does
// not block first paint or compete with the entry animations. Falls back to a
// near-immediate timeout where requestIdleCallback is unavailable (Safari).
const initMixpanel = () => {
try {
  mixpanel.init("5d96be15b728e8689abe73e7fb85f8f1", {
    debug: false, // Set to false to reduce console noise, change to true for debugging
    track_pageview: true,
    persistence: "localStorage",
    autocapture: true,
    // Session replay records the DOM continuously. On a page that mutates every
    // frame via GSAP that is a real main-thread tax and competes with the scroll
    // animations, so sample a fraction of sessions instead of every one. Raise
    // this if you specifically need fuller replay coverage.
    record_sessions_percent: 10,
    ignore_dnt: true, // Ignore Do Not Track settings for development
  });
  
  // Make mixpanel available on window for other parts of the app
  window.mixpanel = mixpanel;
  
  // Helper function to detect device type
  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  // Helper function to get browser name
  const getBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edg')) return 'Edge';
    return 'Other';
  };

  // Helper function to get referrer type
  const getReferrerType = () => {
    const ref = document.referrer;
    if (!ref || ref === '') return 'direct';
    try {
      const refUrl = new URL(ref);
      if (refUrl.hostname === window.location.hostname) return 'internal';
      if (refUrl.hostname.includes('google')) return 'google';
      if (refUrl.hostname.includes('linkedin')) return 'linkedin';
      if (refUrl.hostname.includes('twitter') || refUrl.hostname.includes('x.com')) return 'twitter';
      return 'external';
    } catch {
      return 'external';
    }
  };

  // Verify Mixpanel is loaded and register super properties
  setTimeout(() => {
    if (mixpanel && typeof mixpanel.track === 'function') {
      // Get the distinct_id (auto-generated by Mixpanel)
      const distinctId = mixpanel.get_distinct_id();
      const now = new Date();
      
      // Register super properties that will be sent with every event
      mixpanel.register({
        'first_visit': mixpanel.get_property('$first_visit') || now.toISOString(),
        'last_seen': now.toISOString(),
        'platform': 'web',
        'device_type': getDeviceType(),
        'screen_width': window.screen.width,
        'screen_height': window.screen.height,
        'viewport_width': window.innerWidth,
        'viewport_height': window.innerHeight,
        'browser': getBrowser(),
        'browser_language': navigator.language || 'unknown',
        'referrer_type': getReferrerType(),
        'referrer': document.referrer || 'direct',
        'time_of_day': now.getHours(),
        'day_of_week': now.getDay(),
        'is_mobile': /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
        'is_touch': 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      });
      
      // Identify the user with their distinct_id
      // This ensures all events are properly associated with the user
      mixpanel.identify(distinctId);
      
      // Track page load performance
      if (window.performance && window.performance.timing) {
        const perf = window.performance.timing;
        const pageLoadTime = perf.loadEventEnd - perf.navigationStart;
        const domContentLoaded = perf.domContentLoadedEventEnd - perf.navigationStart;
        
        mixpanel.register_once({
          'avg_page_load_time': pageLoadTime,
        });
      }
      
      // Test event to verify Mixpanel is working
      mixpanel.track('Mixpanel Initialized', {
        timestamp: now.toISOString(),
        page_url: window.location.href,
        distinct_id: distinctId,
      });
      
    }
  }, 100);
} catch (error) {
  // Mixpanel initialization failed
}
};

if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
  window.requestIdleCallback(initMixpanel, { timeout: 3000 });
} else {
  // Safari has no requestIdleCallback; defer just past first paint instead.
  setTimeout(initMixpanel, 1);
}

// Global error handler to suppress the removeChild error
const originalError = console.error;
console.error = function(...args) {
  const message = args[0]?.message || args[0]?.toString?.() || String(args[0]);
  
  // Suppress GSAP/React removeChild errors
  if (
    message?.includes("Failed to execute 'removeChild'") ||
    message?.includes("The node to be removed is not a child of this node") ||
    (args[0] instanceof Error && 
     (args[0].message?.includes("Failed to execute 'removeChild'") ||
      args[0].message?.includes("The node to be removed is not a child")))
  ) {
    return; // Silently suppress
  }
  
  // Track other errors with Mixpanel
  if (mixpanel && typeof mixpanel.track === 'function') {
    try {
      mixpanel.track('Error', {
        error_type: 'javascript',
        error_message: message,
        page_url: window.location.href,
      });
    } catch (e) {
      // Silently fail if Mixpanel tracking fails
    }
  }
  
  // Call original for other errors
  originalError.apply(console, args);
};

// Also override window.onerror to catch errors globally
window.onerror = function(message, source, lineno, colno, error) {
  if (
    message?.includes("Failed to execute 'removeChild'") ||
    message?.includes("The node to be removed is not a child of this node") ||
    error?.message?.includes("Failed to execute 'removeChild'") ||
    error?.message?.includes("The node to be removed is not a child")
  ) {
    return true; // Suppress error
  }
  
  // Track errors with Mixpanel
  if (mixpanel && typeof mixpanel.track === 'function') {
    try {
      mixpanel.track('Error', {
        error_type: 'window_error',
        error_message: message,
        error_source: source,
        error_line: lineno,
        error_column: colno,
        page_url: window.location.href,
      });
    } catch (e) {
      // Silently fail if Mixpanel tracking fails
    }
  }
  
  return false; // Let other errors propagate
};

// Override unhandledrejection to catch Promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (
    event.reason?.message?.includes("Failed to execute 'removeChild'") ||
    event.reason?.message?.includes("The node to be removed is not a child")
  ) {
    event.preventDefault(); // Suppress
    return;
  }
  
  // Track unhandled promise rejections with Mixpanel
  if (mixpanel && typeof mixpanel.track === 'function') {
    try {
      mixpanel.track('Error', {
        error_type: 'unhandled_promise_rejection',
        error_message: event.reason?.message || String(event.reason),
        page_url: window.location.href,
      });
    } catch (e) {
      // Silently fail if Mixpanel tracking fails
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
